export interface ProjectRunContext {
    readonly projectId: string | null;
    readonly loadGeneration: number;
    readonly signal: AbortSignal | null;
}

interface ProjectRunSnapshot {
    projectId?: unknown;
    loadGeneration?: unknown;
    signal?: AbortSignal | null;
}

function normaliseProjectId(value: unknown): string | null {
    const id = typeof value === 'string' ? value.trim() : '';
    return id || null;
}

/** Capture the immutable identity owned by one long-running project operation. */
export function captureProjectRunContext(snapshot: ProjectRunSnapshot = {}): ProjectRunContext {
    const generation = Number(snapshot.loadGeneration);
    return Object.freeze({
        projectId: normaliseProjectId(snapshot.projectId),
        loadGeneration: Number.isFinite(generation) ? generation : 0,
        signal: snapshot.signal || null,
    });
}

/**
 * Strict ownership check used before every state or persistence commit.
 *
 * A named project owns only that exact live id; null/reset is stale. An id-less
 * new project is instead distinguished by its load generation and abort signal,
 * allowing the same run to adopt its first durable id without confusing a reset
 * (which always advances the generation and replaces the controller).
 */
export function isProjectRunContextCurrent(
    context: ProjectRunContext | null | undefined,
    live: ProjectRunSnapshot = {},
): boolean {
    if (!context) return false;
    const liveGeneration = Number(live.loadGeneration);
    if (!Number.isFinite(liveGeneration) || liveGeneration !== context.loadGeneration) return false;
    if (context.signal?.aborted) return false;
    if (context.signal && live.signal !== context.signal) return false;

    const liveProjectId = normaliseProjectId(live.projectId);
    return context.projectId === null || liveProjectId === context.projectId;
}

export function createStaleProjectRunError(label = 'Project operation'): Error & { code: string; retryable: boolean } {
    const error = new Error(`${label} stopped because the active project changed or was reset.`) as Error & {
        code: string;
        retryable: boolean;
    };
    error.name = 'AbortError';
    error.code = 'PROJECT_RUN_STALE';
    error.retryable = true;
    return error;
}
